import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { toDisplayDate } from '@utils';
import {
  CredentialField,
  EBAY_PLATFORM,
  IntegrationConfig,
} from './integrationsConfig';

/**
 * A stored credential's connection status, as returned by GetIntegrations. The backend
 * (MVC + AddNewtonsoftJson) serialises RESPONSES in camelCase, so read-side keys are
 * camelCase — unlike request bodies, which bind case-insensitively.
 */
export interface IntegrationCredential {
  platform: string;
  type?: string;
  connectedAccountUsername?: string | null;
  reconnectBy?: string | null;
  isReconnectNeeded?: boolean;
  isReconnectCheckUnavailable?: boolean;
}

interface ValidationFailureResponse {
  errors?: { errorMessage?: string }[];
}

export type ReconnectDeadline =
  | { status: 'expired' }
  | { status: 'endsSoon' | 'endsLater'; date: string };

const DAYS_OF_WARNING_BEFORE_ACCESS_ENDS = 30;
const EBAY_SIGN_IN_HOSTS = ['auth.ebay.com', 'auth.sandbox.ebay.com'];

export function connectedCredentialsByPlatform(
  credentials: IntegrationCredential[] | undefined | null
): Map<string, IntegrationCredential> {
  return new Map(
    (credentials ?? []).map((credential) => [credential.platform, credential])
  );
}

export function isAvailableToConnect(
  platform: string,
  availablePlatforms: unknown
): boolean {
  return (
    !Array.isArray(availablePlatforms) || availablePlatforms.includes(platform)
  );
}

export function integrationsToShow(
  integrations: IntegrationConfig[],
  availablePlatforms: unknown,
  connectedCredentials: Map<string, IntegrationCredential>
): IntegrationConfig[] {
  return integrations.filter(
    ({ key }) =>
      isAvailableToConnect(key, availablePlatforms) ||
      connectedCredentials.has(key)
  );
}

export function isEbay(config: IntegrationConfig): boolean {
  return config.key === EBAY_PLATFORM;
}

export function isEbaySignInUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && EBAY_SIGN_IN_HOSTS.includes(url.host);
  } catch {
    return false;
  }
}

export function reconnectDeadlineOf(
  reconnectBy: string | null | undefined,
  now: Dayjs = dayjs()
): ReconnectDeadline | null {
  if (!reconnectBy) return null;
  const deadline = dayjs(reconnectBy);
  if (!deadline.isValid()) return null;
  if (!deadline.isAfter(now)) return { status: 'expired' };
  const isEndingWithinTheWarning = deadline.isBefore(
    now.add(DAYS_OF_WARNING_BEFORE_ACCESS_ENDS, 'day')
  );
  return {
    status: isEndingWithinTheWarning ? 'endsSoon' : 'endsLater',
    date: toDisplayDate(deadline),
  };
}

export function isServerError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}

export function firstValidationMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError<ValidationFailureResponse>(error)) return undefined;
  return error.response?.data?.errors?.[0]?.errorMessage;
}

/**
 * Builds the AddIntegration request body. The `type` discriminator MUST be lowercase:
 * CredentialsConverter reads it via the case-sensitive JObject["type"] indexer, whereas
 * the remaining fields are populated case-insensitively and keep their PascalCase C# names.
 * Blank values are omitted so an untouched optional field (e.g. DefaultWarehouseId) never
 * lands on the stored credential as an empty string.
 */
export function buildCredentialPayload(
  config: IntegrationConfig,
  values: Record<string, string>
): { Credentials: Record<string, string> } {
  const suppliedValues = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value.trim().length > 0)
  );
  return {
    Credentials: {
      type: config.credentialType,
      Platform: config.key,
      ...suppliedValues,
    },
  };
}

export function warehousePickerHint(
  field: CredentialField,
  isLoading: boolean,
  hasLoadFailed: boolean,
  optionCount: number,
  contractedMatchCount: number
): string | undefined {
  if (isLoading) return 'Loading your contracted warehouses…';
  if (hasLoadFailed) {
    return 'We could not load your contracted warehouses. Close and reopen this window to try again.';
  }
  if (optionCount === 0 && contractedMatchCount > 0) {
    return 'Your contracted warehouse cannot be selected yet — its Flox profile is not published or verified. Ask your warehouse provider or Flox support to complete it, then reopen this window.';
  }
  if (optionCount === 0) {
    return 'You have no contracted warehouses yet. Contract the warehouse this integration fulfils from, then connect.';
  }
  return field.helperText;
}
